import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Repeat, Search, Filter, FileDown, Building2, User, ChevronRight, ChevronDown, X, Save, Layers, Calendar, Trash2, Upload, AlertCircle, CheckCircle2, Sparkles } from 'lucide-react';
import * as XLSX from 'xlsx';
import { toast } from 'react-hot-toast';
import { generateReclassementDocument } from '../utils/reclassementExport';
import { useThemeStore } from '../stores/themeStore';
import { useConventionsStore } from '../stores/conventionsStore';
import { useOffersStore } from '../stores/offersStore';
import { useBeneficiairesStore } from '../stores/beneficiairesStore';
import { useAuthContext } from '../components/AuthProvider';
import type { Convention, ConventionEmployee, ConventionEmployeeInput, Proposition } from '../types/convention';
import type { JobOffer } from '../types/jobOffer';
import RecommendedOffers from '../components/RecommendedOffers';
import { rankOffers, prepareOffers } from '../utils/jobMatching';

function formatImportDate(value: unknown): string | null {
  if (!value || value === '') return null;
  if (value instanceof Date) return value.toISOString().split('T')[0];
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.split('T')[0];
  const fr = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (fr) return `${fr[3]}-${fr[2]}-${fr[1]}`;
  const d = new Date(s);
  if (!isNaN(d.getTime())) return d.toISOString().split('T')[0];
  return null;
}

function normalizeName(s: string): string {
  return (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
}

function findBeneficiaireForEmployee(employee: ConventionEmployee): { id: string } | null {
  const beneficiaires = useBeneficiairesStore.getState().beneficiaires;
  const bEmail = (employee.email || '').toLowerCase().trim();
  const bFirst = normalizeName(employee.prenom);
  const bLast = normalizeName(employee.nom);

  const match = beneficiaires.find(b => {
    if (bEmail && (b.email || '').toLowerCase().trim() === bEmail) return true;
    return normalizeName(b.firstName) === bFirst && normalizeName(b.lastName) === bLast;
  });
  return match ? { id: match.id } : null;
}

async function removeCandidateFromOffer(
  employee: ConventionEmployee,
  proposition: Proposition,
): Promise<void> {
  const offers = useOffersStore.getState().offers;
  const beneficiaire = findBeneficiaireForEmployee(employee);
  if (!beneficiaire) {
    console.warn('removeCandidateFromOffer: no beneficiaire found for', employee.prenom, employee.nom);
    return;
  }

  const offerId = proposition.offerId;
  const offerCompany = proposition.entreprise || '';
  const offerTitle = proposition.poste || '';

  const offer = offers.find(o =>
    o.id === offerId ||
    (!offerId && normalizeName(o.company || '') === normalizeName(offerCompany) && normalizeName(o.title || '') === normalizeName(offerTitle))
  );
  if (!offer) {
    console.warn('removeCandidateFromOffer: no offer found for', { offerId, offerCompany, offerTitle });
    return;
  }

  const isCandidate = (offer.candidates || []).some(c => c.beneficiaireId === beneficiaire.id);
  if (!isCandidate) {
    console.warn('removeCandidateFromOffer: beneficiaire not in offer candidates', beneficiaire.id, offer.id);
    return;
  }

  try {
    await useOffersStore.getState().removeCandidate(offer.id, beneficiaire.id);
  } catch (err) {
    console.warn('removeCandidateFromOffer: removeCandidate failed', err);
  }
}

async function removeCandidateFromAllOffers(employee: ConventionEmployee): Promise<void> {
  const offers = useOffersStore.getState().offers;
  const beneficiaire = findBeneficiaireForEmployee(employee);
  if (!beneficiaire) {
    console.warn('removeCandidateFromAllOffers: no beneficiaire found for', employee.prenom, employee.nom);
    return;
  }

  const offersWithCandidate = offers.filter(o =>
    (o.candidates || []).some(c => c.beneficiaireId === beneficiaire.id)
  );

  if (offersWithCandidate.length === 0) {
    console.warn('removeCandidateFromAllOffers: no offers contain this candidate');
    return;
  }

  for (const offer of offersWithCandidate) {
    try {
      await useOffersStore.getState().removeCandidate(offer.id, beneficiaire.id);
    } catch (err) {
      console.warn('removeCandidateFromAllOffers: failed for offer', offer.id, err);
    }
  }
}

interface ReclassementRow {
  employee: ConventionEmployee;
  convention: Convention;
}

const statutColors: Record<string, string> = {
  'En recherche d\'emploi': 'bg-amber-100 text-amber-700',
  'Reclassement en CDI': 'bg-green-100 text-green-700',
  'CDD 12 mois': 'bg-emerald-100 text-emerald-700',
  'Process de recrutement': 'bg-blue-100 text-blue-700',
  'Création entreprise réalisée': 'bg-teal-100 text-teal-700',
  'Création entreprise en projet': 'bg-cyan-100 text-cyan-700',
  'Formations longues en cours': 'bg-indigo-100 text-indigo-700',
  'Formations longues en attente': 'bg-violet-100 text-violet-700',
  'Formations courtes en cours': 'bg-sky-100 text-sky-700',
  'Formations courtes en attentes': 'bg-slate-100 text-slate-700',
  'Formations courtes en attente de validation': 'bg-gray-100 text-gray-700',
  'Congé de maternité': 'bg-pink-100 text-pink-700',
  'En attente de retraite': 'bg-orange-100 text-orange-700',
  'Sortie': 'bg-red-100 text-red-700',
};

const statutDarkColors: Record<string, string> = {
  'En recherche d\'emploi': 'bg-amber-900/40 text-amber-300',
  'Reclassement en CDI': 'bg-green-900/40 text-green-300',
  'CDD 12 mois': 'bg-emerald-900/40 text-emerald-300',
  'Process de recrutement': 'bg-blue-900/40 text-blue-300',
  'Création entreprise réalisée': 'bg-teal-900/40 text-teal-300',
  'Création entreprise en projet': 'bg-cyan-900/40 text-cyan-300',
  'Formations longues en cours': 'bg-indigo-900/40 text-indigo-300',
  'Formations longues en attente': 'bg-violet-900/40 text-violet-300',
  'Formations courtes en cours': 'bg-sky-900/40 text-sky-300',
  'Formations courtes en attentes': 'bg-slate-900/40 text-slate-300',
  'Formations courtes en attente de validation': 'bg-gray-900/40 text-gray-300',
  'Congé de maternité': 'bg-pink-900/40 text-pink-300',
  'En attente de retraite': 'bg-orange-900/40 text-orange-300',
  'Sortie': 'bg-red-900/40 text-red-300',
};

const propositionFields: { key: keyof Proposition; label: string }[] = [
  { key: 'entreprise', label: 'Entreprise' },
  { key: 'poste', label: 'Poste proposé' },
  { key: 'contrat', label: 'Contrat' },
  { key: 'cv_envoye', label: 'CV envoyé' },
  { key: 'entretien', label: 'Entretien' },
  { key: 'resultat', label: 'Résultat' },
  { key: 'ove', label: 'OVE' },
];

const propositionColumnKeys = ['entreprise', 'poste', 'contrat', 'cv_envoye', 'entretien', 'resultat', 'ove'] as const;

function computeAge(dateNaissance: string | null, age: number | null = null): string {
  if (age !== null && age !== undefined) return `${age} ans`;
  if (!dateNaissance) return '—';
  const birth = new Date(dateNaissance);
  if (isNaN(birth.getTime())) return '—';
  const today = new Date();
  let years = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) years--;
  return `${years} ans`;
}

function getPropositions(employee: ConventionEmployee): Proposition[] {
  if (employee.propositions && employee.propositions.length > 0) return employee.propositions;
  return [];
}

const AI_OFFER_PREFIX = 'ai:';

function Reclassement() {
  const { darkMode } = useThemeStore();
  const { user } = useAuthContext();
  const { conventions, loading, loadConventions, loadAllEmployees, employees, updateEmployee, bulkAddEmployees } = useConventionsStore();
  const { loadOffers, offers, aiOffers, loadAiOffers, importAiOffer } = useOffersStore();
  const { loadBeneficiaires } = useBeneficiairesStore();
  const [search, setSearch] = useState('');
  const [filterStatut, setFilterStatut] = useState<string>('all');
  const [filterConvention, setFilterConvention] = useState<string>('all');
  const [filterEntreprise, setFilterEntreprise] = useState<string>('all');
  const [filterContrat, setFilterContrat] = useState<string>('all');
  const [filterEntretien, setFilterEntretien] = useState<string>('all');
  const [filterResultat, setFilterResultat] = useState<string>('all');
  const [filterOve, setFilterOve] = useState<string>('all');
  const [filterCommission, setFilterCommission] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [editingCell, setEditingCell] = useState<{ id: string; field: string; propIndex: number } | null>(null);
  const [editValue, setEditValue] = useState('');
  const [selectedRow, setSelectedRow] = useState<ReclassementRow | null>(null);
  const [ageInputMode, setAgeInputMode] = useState<'date' | 'age'>('date');
  const [syncingCleanup, setSyncingCleanup] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importConventionId, setImportConventionId] = useState<string>('');
  const [importPreview, setImportPreview] = useState<ConventionEmployeeInput[]>([]);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!user) return;
    loadConventions();
  }, [user, loadConventions]);

  useEffect(() => {
    if (conventions.length > 0) {
      loadAllEmployees();
    }
  }, [conventions, loadAllEmployees]);

  useEffect(() => {
    if (!user) return;
    loadOffers();
    loadAiOffers();
    loadBeneficiaires();
  }, [user, loadOffers, loadBeneficiaires]);

  const rows: ReclassementRow[] = useMemo(() => {
    return conventions.flatMap(c =>
      employees
        .filter(e => e.convention_id === c.id)
        .map(e => ({ employee: e, convention: c }))
    );
  }, [conventions, employees]);

  const preparedOffers = useMemo(() => prepareOffers([
    ...offers,
    ...aiOffers
      .filter(o => !o.imported_to_offers)
      .map(o => ({
        id: `${AI_OFFER_PREFIX}${o.id}`,
        title: o.title,
        company: o.company,
        location: o.location || '',
        type: o.type || '',
        urgencyLevel: 'medium' as const,
        status: 'new' as const,
        description: o.description || '',
        skills: o.skills || [],
        candidates: [],
        createdAt: o.created_at,
        aiSource: o.source,
        aiMatchedPoste: o.matched_keyword,
        isAiGenerated: true,
      })),
  ]), [offers, aiOffers]);

  const matchSummaries = useMemo(() => {
    const map = new Map<string, { count: number; best: number }>();
    rows.forEach(({ employee }) => {
      const excluded = new Set(getPropositions(employee).map(p => p.offerId).filter(Boolean));
      const matches = rankOffers(employee, preparedOffers, excluded);
      if (matches.length > 0) map.set(employee.id, { count: matches.length, best: matches[0].score });
    });
    return map;
  }, [rows, preparedOffers]);

  const openEmployee = (e: ConventionEmployee, c: Convention) => {
    setSelectedRow({ employee: e, convention: c });
    setAgeInputMode(e.age !== null && e.age !== undefined && !e.date_naissance ? 'age' : 'date');
  };

  const uniqueEntreprises = useMemo(() => {
    const set = new Set<string>();
    rows.forEach(r => {
      if (r.convention.entreprise_nom) set.add(r.convention.entreprise_nom);
      getPropositions(r.employee).forEach(p => { if (p.entreprise) set.add(p.entreprise); });
    });
    return Array.from(set).sort();
  }, [rows]);

  const uniqueContrats = useMemo(() => {
    const set = new Set<string>();
    rows.forEach(r => getPropositions(r.employee).forEach(p => { if (p.contrat) set.add(p.contrat); }));
    return Array.from(set).sort();
  }, [rows]);

  const uniqueEntretiens = useMemo(() => {
    const set = new Set<string>();
    rows.forEach(r => getPropositions(r.employee).forEach(p => { if (p.entretien) set.add(p.entretien); }));
    return Array.from(set).sort();
  }, [rows]);

  const uniqueResultats = useMemo(() => {
    const set = new Set<string>();
    rows.forEach(r => getPropositions(r.employee).forEach(p => { if (p.resultat) set.add(p.resultat); }));
    return Array.from(set).sort();
  }, [rows]);

  const uniqueCommissions = useMemo(() => {
    const set = new Set<string>();
    rows.forEach(r => { if (r.employee.commission) set.add(r.employee.commission); });
    return Array.from(set).sort();
  }, [rows]);

  const hasActiveFilters = filterStatut !== 'all' || filterConvention !== 'all' || filterEntreprise !== 'all' || filterContrat !== 'all' || filterEntretien !== 'all' || filterResultat !== 'all' || filterOve !== 'all' || filterCommission !== 'all';

  const resetFilters = () => {
    setFilterStatut('all');
    setFilterConvention('all');
    setFilterEntreprise('all');
    setFilterContrat('all');
    setFilterEntretien('all');
    setFilterResultat('all');
    setFilterOve('all');
    setFilterCommission('all');
  };

  const filtered = rows.filter(r => {
    const fullName = `${r.employee.prenom} ${r.employee.nom}`.toLowerCase();
    const matchSearch = fullName.includes(search.toLowerCase()) ||
      r.convention.entreprise_nom.toLowerCase().includes(search.toLowerCase()) ||
      r.employee.poste.toLowerCase().includes(search.toLowerCase());
    const matchStatut = filterStatut === 'all' || r.employee.statut === filterStatut;
    const matchConvention = filterConvention === 'all' || r.convention.id === filterConvention;
    const props = getPropositions(r.employee);
    const matchEntreprise = filterEntreprise === 'all' ||
      r.convention.entreprise_nom === filterEntreprise ||
      props.some(p => p.entreprise === filterEntreprise);
    const matchContrat = filterContrat === 'all' || props.some(p => p.contrat === filterContrat);
    const matchEntretien = filterEntretien === 'all' || props.some(p => p.entretien === filterEntretien);
    const matchResultat = filterResultat === 'all' || props.some(p => p.resultat === filterResultat);
    const matchOve = filterOve === 'all' || props.some(p => p.ove === filterOve);
    const matchCommission = filterCommission === 'all' || r.employee.commission === filterCommission;
    return matchSearch && matchStatut && matchConvention && matchEntreprise && matchContrat && matchEntretien && matchResultat && matchOve && matchCommission;
  });

  const statuts = [
    'En recherche d\'emploi',
    'Reclassement en CDI',
    'CDD 12 mois',
    'Process de recrutement',
    'Création entreprise réalisée',
    'Création entreprise en projet',
    'Formations longues en cours',
    'Formations longues en attente',
    'Formations courtes en cours',
    'Formations courtes en attentes',
    'Formations courtes en attente de validation',
    'Congé de maternité',
    'En attente de retraite',
    'Sortie',
  ];

  const statutCounts = useMemo(() => rows
    .filter(row => filterConvention === 'all' || row.convention.id === filterConvention)
    .reduce<Record<string, number>>((counts, row) => {
      counts[row.employee.statut] = (counts[row.employee.statut] || 0) + 1;
      return counts;
    }, {}), [rows, filterConvention]);

  const handleExport = async () => {
    if (filtered.length === 0) {
      toast.error('Aucun candidat à exporter');
      return;
    }
    try {
      await generateReclassementDocument(filtered);
      toast.success(`${filtered.length} candidat${filtered.length > 1 ? 's' : ''} exporté${filtered.length > 1 ? 's' : ''}`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Erreur lors de la génération du document');
    }
  };

  const handleImportFileSelected = async (file: File) => {
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows: Record<string, unknown>[] = XLSX.utils.sheet_to_json(ws, { defval: '' });
      const normalizeHeader = (value: string) => value
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]/g, '');
      const getValue = (row: Record<string, unknown>, ...names: string[]) => {
        const normalizedNames = names.map(normalizeHeader);
        const key = Object.keys(row).find(rowKey => normalizedNames.includes(normalizeHeader(rowKey)));
        return key ? row[key] : '';
      };
      const parseAge = (raw: unknown): number | null => {
        if (raw === '' || raw == null) return null;
        if (typeof raw === 'number') return raw >= 18 && raw <= 100 ? raw : null;
        if (raw instanceof Date) return null;
        const s = String(raw).trim();
        const m = s.match(/\d+/);
        if (!m) return null;
        const n = Number(m[0]);
        return n >= 18 && n <= 100 ? n : null;
      };
      const employeesData: ConventionEmployeeInput[] = rows.map(r => {
        const allValues = Object.values(r);
        const importedAge = allValues
          .map(v => parseAge(v))
          .find(value => value !== null && value >= 18 && value <= 100) ?? null;
        return {
          nom: String(getValue(r, 'Nom', 'nom')),
          prenom: String(getValue(r, 'Prénom', 'prenom')),
          telephone: String(getValue(r, 'Tél', 'Téléphone', 'telephone')),
          telephone_portable: String(getValue(r, 'Portable', 'telephone_portable')),
          email: String(getValue(r, 'Email', 'email')),
          poste: String(getValue(r, 'Poste occupé', 'Poste actuel', 'Poste', 'poste')),
          poste_actuel: String(getValue(r, 'Poste occupé', 'Poste actuel', 'Poste', 'poste_actuel')),
          statut: (getValue(r, 'Statut', 'statut') || 'En recherche d\'emploi') as ConventionEmployeeInput['statut'],
          date_entree: formatImportDate(getValue(r, 'Date de début', "Date d'entrée", 'date_entree')),
          date_fin: formatImportDate(getValue(r, 'Date de fin', 'date_fin')),
          consultant_attribue: String(getValue(r, 'Consultant attribué', 'Consultant', 'consultant_attribue')),
          notes: String(getValue(r, 'Commentaires Commission', 'Notes', 'notes')),
          date_naissance: formatImportDate(getValue(r, 'Date de naissance', 'date_naissance')),
          age: importedAge,
          ville_dept: String(getValue(r, 'Ville / Dept', 'Ville', 'ville_dept')),
          commission: String(getValue(r, 'Commission', 'Numero commission', 'Numéro commission', 'Mois / année', 'Mois / annee', 'commission')),
        };
      }).filter(e => e.nom || e.prenom);
      if (employeesData.length === 0) {
        toast.error('Aucun salarié trouvé dans le fichier');
        return;
      }
      setImportPreview(employeesData);
    } catch {
      toast.error('Erreur lors de la lecture du fichier');
    }
  };

  const handleConfirmImport = async () => {
    if (!importConventionId || importPreview.length === 0 || importing) return;
    setImporting(true);
    try {
      const result = await bulkAddEmployees(importConventionId, importPreview);
      const parts: string[] = [];
      if (result.created > 0) parts.push(`${result.created} nouveau${result.created > 1 ? 'x' : ''}`);
      if (result.updated > 0) parts.push(`${result.updated} mis à jour`);
      toast.success(parts.length > 0 ? parts.join(', ') : 'Aucun changement');
      setImportPreview([]);
      setShowImportModal(false);
      setImportConventionId('');
    } catch {
      toast.error('Erreur lors de l\'import des salariés');
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadImportTemplate = () => {
    const ws = XLSX.utils.json_to_sheet([
      { Nom: 'Dupont', Prénom: 'Jean', Tél: '0102030405', Portable: '0607080910', Email: 'jean.dupont@example.com', 'Poste occupé': 'Chargé de mission', Âge: 45, 'Ville / Dept': 'Paris 75', 'Date de début': '2026-01-01', 'Date de fin': '2026-12-31', 'Consultant attribué': '', Statut: 'En recherche d\'emploi', 'Commentaires Commission': '' },
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Salariés');
    XLSX.writeFile(wb, 'template_import_salaries.xlsx');
  };

  const startEdit = (id: string, field: string, currentValue: string, propIndex: number) => {
    setEditingCell({ id, field, propIndex });
    setEditValue(currentValue || '');
  };

  const saveEdit = async () => {
    if (!editingCell) return;
    try {
      const emp = employees.find(e => e.id === editingCell.id);
      if (!emp) return;

      if (editingCell.propIndex === -1) {
        let fieldValue: string | number | null;
        if (editingCell.field === 'age') {
          fieldValue = editValue === '' ? null : Number(editValue);
        } else if (editingCell.field === 'statut') {
          fieldValue = editValue || 'En recherche d\'emploi';
        } else {
          fieldValue = editValue === '' ? null : editValue;
        }
        await updateEmployee(editingCell.id, {
          [editingCell.field]: fieldValue,
        } as Partial<ConventionEmployee>);
      } else {
        const props = getPropositions(emp);
        const updatedProps = props.map((p, i) =>
          i === editingCell.propIndex
            ? { ...p, [editingCell.field]: editValue }
            : p
        );

        await updateEmployee(editingCell.id, {
          propositions: updatedProps,
          [editingCell.field]: updatedProps[0]?.[editingCell.field as keyof Proposition] || '',
        } as Partial<ConventionEmployee>);
      }
      if (editingCell.propIndex === -1) {
        const updatedValue = editingCell.field === 'age'
          ? (editValue ? Number(editValue) : null)
          : editingCell.field === 'statut'
          ? (editValue || 'En recherche d\'emploi')
          : editValue;
        setSelectedRow(current => current
          ? { ...current, employee: { ...current.employee, [editingCell.field]: updatedValue } as ConventionEmployee }
          : current
        );
      }
      toast.success('Mis à jour');
    } catch {
      toast.error('Erreur lors de la mise à jour');
    }
    setEditingCell(null);
    setEditValue('');
  };

  const addRecommendedOffer = async (employee: ConventionEmployee, offer: JobOffer) => {
    let offerId = offer.id;
    if (offerId.startsWith(AI_OFFER_PREFIX)) {
      try {
        const importedId = await importAiOffer(offerId.slice(AI_OFFER_PREFIX.length));
        if (!importedId) return;
        offerId = importedId;
      } catch {
        return;
      }
    }
    const newProp: Proposition = {
      offerId,
      entreprise: offer.company || '',
      poste: offer.title || '',
      contrat: offer.type || '',
      cv_envoye: '',
      entretien: '',
      resultat: '',
      ove: '',
      notes: '',
    };
    const updatedProps = [...getPropositions(employee), newProp];
    const updates: Partial<ConventionEmployee> = {
      propositions: updatedProps,
      entreprise: updatedProps[0].entreprise,
      poste: updatedProps[0].poste,
      contrat: updatedProps[0].contrat,
    };
    try {
      await updateEmployee(employee.id, updates);
      setSelectedRow(current => current && current.employee.id === employee.id
        ? { ...current, employee: { ...current.employee, ...updates } }
        : current
      );
      toast.success('Offre ajoutée aux propositions');
    } catch {
      toast.error('Erreur lors de l\'ajout de la proposition');
    }
  };

  const cancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const deleteProposition = async (employeeId: string, propIndex: number) => {
    try {
      const emp = employees.find(e => e.id === employeeId);
      if (!emp) return;
      const props = getPropositions(emp);
      if (propIndex < 0 || propIndex >= props.length) return;
      const deletedProp = props[propIndex];
      const updatedProps = props.filter((_, i) => i !== propIndex);
      const update: Partial<ConventionEmployee> = { propositions: updatedProps };
      if (updatedProps.length === 0) {
        update.entreprise = null;
        update.poste = null;
        update.contrat = null;
        update.cv_envoye = null;
        update.entretien = null;
        update.resultat = null;
        update.ove = null;
        update.notes = null;
      } else {
        const p0 = updatedProps[0];
        update.entreprise = p0.entreprise || null;
        update.poste = p0.poste || null;
        update.contrat = p0.contrat || null;
        update.cv_envoye = p0.cv_envoye || null;
        update.entretien = p0.entretien || null;
        update.resultat = p0.resultat || null;
        update.ove = p0.ove || null;
        update.notes = p0.notes || null;
      }
      await updateEmployee(employeeId, update);
      setSelectedRow(current => current && current.employee.id === employeeId
        ? { ...current, employee: { ...current.employee, ...update } as ConventionEmployee }
        : current
      );
      try {
        await removeCandidateFromOffer(emp, deletedProp);
      } catch (syncErr) {
        console.warn('Sync to offers failed:', syncErr);
      }
      toast.success('Proposition supprimée');
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const inputClass = `w-full px-2 py-1 rounded text-sm border ${darkMode ? 'bg-gray-700 border-gray-500 text-white' : 'bg-white border-blue-400 text-gray-900'} focus:outline-none focus:ring-1 focus:ring-blue-500`;

  const renderMultiCell = (employee: ConventionEmployee, field: keyof Proposition) => {
    const props = getPropositions(employee);
    if (props.length === 0) {
      return <span className="text-gray-400 italic text-sm">—</span>;
    }

    const isOveField = field === 'ove';

    return (
      <div className="space-y-1">
        {props.map((p, i) => {
          const value = p[field] as string;
          const isEditing = editingCell?.id === employee.id && editingCell?.field === field && editingCell?.propIndex === i;
          if (isEditing) {
            if (isOveField) {
              return (
                <div key={i} className="flex items-center gap-1">
                  <select
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveEdit();
                      if (e.key === 'Escape') cancelEdit();
                    }}
                    autoFocus
                    className={inputClass}
                  >
                    <option value="">—</option>
                    <option value="OUI">OUI</option>
                    <option value="NON">NON</option>
                  </select>
                  <button onClick={saveEdit} className={`p-1 rounded ${darkMode ? 'text-green-400 hover:bg-gray-600' : 'text-green-600 hover:bg-green-50'}`}>
                    <Save className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={cancelEdit} className={`p-1 rounded ${darkMode ? 'text-red-400 hover:bg-gray-600' : 'text-red-600 hover:bg-red-50'}`}>
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            }
            return (
              <div key={i} className="flex items-center gap-1">
                <input
                  type="text"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveEdit();
                    if (e.key === 'Escape') cancelEdit();
                  }}
                  autoFocus
                  className={inputClass}
                />
                <button onClick={saveEdit} className={`p-1 rounded ${darkMode ? 'text-green-400 hover:bg-gray-600' : 'text-green-600 hover:bg-green-50'}`}>
                  <Save className="w-3.5 h-3.5" />
                </button>
                <button onClick={cancelEdit} className={`p-1 rounded ${darkMode ? 'text-red-400 hover:bg-gray-600' : 'text-red-600 hover:bg-red-50'}`}>
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          }
          if (isOveField) {
            const badgeColor = value === 'OUI'
              ? (darkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-700')
              : value === 'NON'
              ? (darkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-700')
              : '';
            return (
              <div
                key={i}
                onClick={() => startEdit(employee.id, field, value, i)}
                className={`block cursor-pointer text-sm px-2 py-1 rounded transition-colors ${value ? `inline-block font-semibold ${badgeColor}` : `${darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'}`}`}
              >
                {value ? value : <span className="text-gray-400 italic">—</span>}
              </div>
            );
          }
          return (
            <div
              key={i}
              onClick={() => startEdit(employee.id, field, value, i)}
              className={`block cursor-pointer text-sm px-2 py-1 rounded ${darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'} transition-colors`}
            >
              {value ? value : <span className="text-gray-400 italic">—</span>}
            </div>
          );
        })}
      </div>
    );
  };

  const renderAnnotationsCell = (employee: ConventionEmployee) => {
    const annotations = employee.annotations_cdp || '';
    const isEditing = editingCell?.id === employee.id && editingCell?.field === 'annotations_cdp' && editingCell?.propIndex === -1;
    if (isEditing) {
      return (
        <div className="flex flex-col gap-1">
          <textarea
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) saveEdit();
              if (e.key === 'Escape') cancelEdit();
            }}
            autoFocus
            rows={4}
            placeholder="Annotations internes (non visibles sur l'export)..."
            className={`w-[280px] min-w-[280px] min-h-[100px] px-2 py-1 rounded text-sm border resize-y break-words ${darkMode ? 'bg-gray-700 border-gray-500 text-white placeholder-gray-400' : 'bg-white border-amber-400 text-gray-900 placeholder-gray-400'} focus:outline-none focus:ring-1 focus:ring-amber-500`}
          />
          <div className="flex items-center gap-1">
            <button onClick={saveEdit} className={`p-1 rounded ${darkMode ? 'text-green-400 hover:bg-gray-600' : 'text-green-600 hover:bg-green-50'}`}>
              <Save className="w-3.5 h-3.5" />
            </button>
            <button onClick={cancelEdit} className={`p-1 rounded ${darkMode ? 'text-red-400 hover:bg-gray-600' : 'text-red-600 hover:bg-red-50'}`}>
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      );
    }
    return (
      <div
        onClick={() => startEdit(employee.id, 'annotations_cdp', annotations, -1)}
        className={`block cursor-pointer text-sm px-2 py-1 rounded whitespace-pre-wrap min-h-[60px] break-words ${darkMode ? 'text-amber-200 hover:bg-gray-700' : 'text-amber-900 hover:bg-amber-50'} transition-colors`}
      >
        {annotations ? annotations : <span className="text-gray-400 italic">Cliquer pour ajouter des annotations</span>}
      </div>
    );
  };

  const renderNotesCell = (employee: ConventionEmployee) => {
    const props = getPropositions(employee);
    const notes = employee.notes || props[0]?.notes || '';
    const isEditing = editingCell?.id === employee.id && editingCell?.field === 'notes' && editingCell?.propIndex === -1;
    if (isEditing) {
      return (
        <div className="flex flex-col gap-1">
          <textarea
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) saveEdit();
              if (e.key === 'Escape') cancelEdit();
            }}
            autoFocus
            rows={4}
            placeholder="Saisir les commentaires commission ici..."
            className={`w-[320px] min-w-[320px] min-h-[100px] px-2 py-1 rounded text-sm border resize-y break-words ${darkMode ? 'bg-gray-700 border-gray-500 text-white placeholder-gray-400' : 'bg-white border-blue-400 text-gray-900 placeholder-gray-400'} focus:outline-none focus:ring-1 focus:ring-blue-500`}
          />
          <div className="flex items-center gap-1">
            <button onClick={saveEdit} className={`p-1 rounded ${darkMode ? 'text-green-400 hover:bg-gray-600' : 'text-green-600 hover:bg-green-50'}`}>
              <Save className="w-3.5 h-3.5" />
            </button>
            <button onClick={cancelEdit} className={`p-1 rounded ${darkMode ? 'text-red-400 hover:bg-gray-600' : 'text-red-600 hover:bg-red-50'}`}>
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      );
    }
    return (
      <div
        onClick={() => startEdit(employee.id, 'notes', notes, -1)}
        className={`block cursor-pointer text-sm px-2 py-1 rounded whitespace-pre-wrap min-h-[60px] break-words ${darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'} transition-colors`}
      >
        {notes ? notes : <span className="text-gray-400 italic">Cliquer pour ajouter des commentaires commission</span>}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className={`relative rounded-2xl overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
        <div className="bg-gradient-anthea p-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-3">
              <Repeat className="w-8 h-8 text-white" />
              <div>
                <h1 className="text-2xl font-bold text-white">Reclassement</h1>
                <p className="text-white/80 text-sm">Suivi des candidats accompagnés par convention</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => setShowImportModal(true)}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-white/20 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-white/80"
              >
                <Upload className="h-4 w-4" />
                Importer
              </button>
              <button
                onClick={handleExport}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-[#1d3557] shadow-sm transition hover:bg-[#fff4ca] focus:outline-none focus:ring-2 focus:ring-white/80"
              >
                <FileDown className="h-4 w-4" />
                Exporter le suivi
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className={`rounded-2xl shadow-sm p-4 max-w-full overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="flex flex-col gap-3 mb-4">
          <div className="relative w-full">
            <Search className={`absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 ${darkMode ? 'text-gray-400' : 'text-gray-400'}`} />
            <input
              type="text"
              placeholder="Rechercher un candidat, entreprise, poste..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={`w-full pl-12 pr-4 py-4 text-base rounded-xl border ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'} focus:outline-none focus:ring-2 focus:ring-anthea-blue`}
            />
          </div>
          <div className="flex items-center justify-between">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 text-sm font-medium transition-colors ${darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
            >
              <Filter className="w-4 h-4" />
              Filtres
              {hasActiveFilters && <span className="inline-block w-2 h-2 rounded-full bg-anthea-blue" />}
              <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>
            {hasActiveFilters && (
              <button
                onClick={resetFilters}
                className={`text-xs transition-colors ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
              >
                Réinitialiser
              </button>
            )}
          </div>
          {showFilters && (
            <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 p-3 rounded-xl ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
              <div>
                <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Convention</label>
                <select
                  value={filterConvention}
                  onChange={(e) => setFilterConvention(e.target.value)}
                  className={`w-full px-2 py-1.5 rounded-lg border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'} focus:outline-none focus:ring-1 focus:ring-anthea-blue`}
                >
                  <option value="all">Toutes les conventions</option>
                  {conventions.map(c => (
                    <option key={c.id} value={c.id}>{c.entreprise_nom || c.nom}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Statut</label>
                <select
                  value={filterStatut}
                  onChange={(e) => setFilterStatut(e.target.value)}
                  className={`w-full px-2 py-1.5 rounded-lg border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'} focus:outline-none focus:ring-1 focus:ring-anthea-blue`}
                >
                  <option value="all">Tous les statuts ({rows.length})</option>
                  {statuts.map(s => (
                    <option key={s} value={s}>{statutCounts[s] || 0} — {s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Entreprise</label>
                <select
                  value={filterEntreprise}
                  onChange={(e) => setFilterEntreprise(e.target.value)}
                  className={`w-full px-2 py-1.5 rounded-lg border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'} focus:outline-none focus:ring-1 focus:ring-anthea-blue`}
                >
                  <option value="all">Toutes les entreprises</option>
                  {uniqueEntreprises.map(e => (
                    <option key={e} value={e}>{e}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Contrat</label>
                <select
                  value={filterContrat}
                  onChange={(e) => setFilterContrat(e.target.value)}
                  className={`w-full px-2 py-1.5 rounded-lg border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'} focus:outline-none focus:ring-1 focus:ring-anthea-blue`}
                >
                  <option value="all">Tous les contrats</option>
                  {uniqueContrats.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Entretien</label>
                <select
                  value={filterEntretien}
                  onChange={(e) => setFilterEntretien(e.target.value)}
                  className={`w-full px-2 py-1.5 rounded-lg border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'} focus:outline-none focus:ring-1 focus:ring-anthea-blue`}
                >
                  <option value="all">Tous les entretiens</option>
                  {uniqueEntretiens.map(e => (
                    <option key={e} value={e}>{e}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Résultat</label>
                <select
                  value={filterResultat}
                  onChange={(e) => setFilterResultat(e.target.value)}
                  className={`w-full px-2 py-1.5 rounded-lg border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'} focus:outline-none focus:ring-1 focus:ring-anthea-blue`}
                >
                  <option value="all">Tous les résultats</option>
                  {uniqueResultats.map(r => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>OVE</label>
                <select
                  value={filterOve}
                  onChange={(e) => setFilterOve(e.target.value)}
                  className={`w-full px-2 py-1.5 rounded-lg border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'} focus:outline-none focus:ring-1 focus:ring-anthea-blue`}
                >
                  <option value="all">Tous les OVE</option>
                  <option value="OUI">OUI</option>
                  <option value="NON">NON</option>
                </select>
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Commission</label>
                <select
                  value={filterCommission}
                  onChange={(e) => setFilterCommission(e.target.value)}
                  className={`w-full px-2 py-1.5 rounded-lg border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'} focus:outline-none focus:ring-1 focus:ring-anthea-blue`}
                >
                  <option value="all">Toutes les commissions</option>
                  {uniqueCommissions.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {rows.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {statuts.map(s => {
              const count = statutCounts[s] || 0;
              if (count === 0) return null;
              const isActive = filterStatut === s;
              return (
                <button
                  key={s}
                  onClick={() => setFilterStatut(isActive ? 'all' : s)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                    isActive
                      ? 'ring-2 ring-anthea-blue scale-105 '
                      : ''
                  }${darkMode ? statutDarkColors[s] : statutColors[s]}`}
                >
                  <span className="font-bold">{count}</span>
                  <span>{s}</span>
                </button>
              );
            })}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-anthea-blue"></div>
          </div>
        ) : (
          <div className="overflow-x-auto overflow-y-auto max-h-[70vh] sticky-left-0">
            <table className="w-full min-w-max table-auto">
              <thead className="sticky top-0 z-10">
                <tr className={`text-left text-xs uppercase ${darkMode ? 'text-gray-400 bg-gray-800' : 'text-gray-500 bg-white'} border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <th className="py-3 px-3 font-semibold whitespace-nowrap">Candidat</th>
                  <th className="py-3 px-3 font-semibold whitespace-nowrap">Âge</th>
                  <th className="py-3 px-3 font-semibold whitespace-nowrap">Ville / Dept</th>
                  <th className="py-3 px-3 font-semibold whitespace-nowrap">Convention</th>
                  <th className="py-3 px-3 font-semibold whitespace-nowrap">Poste actuel</th>
                  <th className="py-3 px-3 font-semibold whitespace-nowrap">Projet validé</th>
                  <th className="py-3 px-3 font-semibold whitespace-nowrap">Statut</th>
                  <th className="py-3 px-3 font-semibold whitespace-nowrap">Commission</th>
                  {propositionFields.map(f => (
                    <th key={f.key} className="py-3 px-3 font-semibold whitespace-nowrap">{f.label}</th>
                  ))}
                  <th className="py-3 px-3 font-semibold whitespace-nowrap">Commentaires Commission</th>
                  <th className="py-3 px-3 font-semibold whitespace-nowrap">Annotations CDP</th>
                  <th className="py-3 px-3 font-semibold"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(({ employee: e, convention: c }) => {
                  const props = getPropositions(e);
                  return (
                    <tr key={e.id} className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-100'} transition-colors`}>
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-2">
                          <User className={`w-4 h-4 shrink-0 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                          <div className="flex flex-col">
                            <button
                              onClick={() => openEmployee(e, c)}
                              className={`text-left text-sm font-medium hover:underline ${darkMode ? 'text-white hover:text-sky-300' : 'text-gray-900 hover:text-sky-700'}`}
                            >
                              {e.prenom} {e.nom}
                            </button>
                            {matchSummaries.get(e.id) && (
                              <button
                                onClick={() => openEmployee(e, c)}
                                title="Voir les offres recommandées"
                                className={`mt-1 inline-flex w-fit items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium transition-colors ${darkMode ? 'bg-sky-900/40 text-sky-300 hover:bg-sky-900/70' : 'bg-sky-50 text-sky-700 hover:bg-sky-100'}`}
                              >
                                <Sparkles className="w-3 h-3" />
                                {matchSummaries.get(e.id)!.count} offre{matchSummaries.get(e.id)!.count > 1 ? 's' : ''} · {matchSummaries.get(e.id)!.best}%
                              </button>
                            )}
                            {props.length > 1 && (
                              <span className={`inline-flex items-center gap-1 text-xs ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                                <Layers className="w-3 h-3" />
                                {props.length} propositions
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-2 px-3">
                        <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{computeAge(e.date_naissance, e.age)}</span>
                      </td>
                      <td className="py-2 px-3">
                        <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{e.ville_dept || '—'}</span>
                      </td>
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-2">
                          <Building2 className={`w-4 h-4 shrink-0 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                          <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{c.nom}</span>
                        </div>
                      </td>
                      <td className="py-2 px-3">
                        <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{e.poste_actuel || '—'}</span>
                      </td>
                      <td className="py-2 px-3 min-w-[180px] align-top">
                        {editingCell?.id === e.id && editingCell?.field === 'projet_valide' ? (
                          <div className="flex items-center gap-1">
                            <input
                              type="text"
                              value={editValue}
                              onChange={(ev) => setEditValue(ev.target.value)}
                              onKeyDown={(ev) => {
                                if (ev.key === 'Enter') saveEdit();
                                if (ev.key === 'Escape') cancelEdit();
                              }}
                              autoFocus
                              placeholder="Projet validé"
                              className={inputClass}
                            />
                            <button onClick={saveEdit} className={`p-1 rounded ${darkMode ? 'text-green-400 hover:bg-gray-600' : 'text-green-600 hover:bg-green-50'}`}>
                              <Save className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={cancelEdit} className={`p-1 rounded ${darkMode ? 'text-red-400 hover:bg-gray-600' : 'text-red-600 hover:bg-red-50'}`}>
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ) : (
                          <div
                            onClick={() => startEdit(e.id, 'projet_valide', e.projet_valide || '', -1)}
                            className={`cursor-pointer px-2 py-1 rounded text-sm ${darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'} transition-colors`}
                          >
                            {e.projet_valide || <span className="text-gray-400 italic">Ajouter</span>}
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-3">
                        <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${darkMode ? statutDarkColors[e.statut] : statutColors[e.statut]}`}>{e.statut}</span>
                      </td>
                      <td className="py-2 px-3 min-w-[150px] align-top">
                        {editingCell?.id === e.id && editingCell?.field === 'commission' && !selectedRow ? (
                          <div className="flex items-center gap-1">
                            <input
                              type="text"
                              value={editValue}
                              onChange={(ev) => setEditValue(ev.target.value)}
                              onKeyDown={(ev) => {
                                if (ev.key === 'Enter') saveEdit();
                                if (ev.key === 'Escape') cancelEdit();
                              }}
                              autoFocus
                              placeholder="N° ou mois/année"
                              className={inputClass}
                            />
                            <button onClick={saveEdit} className={`p-1 rounded ${darkMode ? 'text-green-400 hover:bg-gray-600' : 'text-green-600 hover:bg-green-50'}`}>
                              <Save className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={cancelEdit} className={`p-1 rounded ${darkMode ? 'text-red-400 hover:bg-gray-600' : 'text-red-600 hover:bg-red-50'}`}>
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ) : (
                          <div
                            onClick={() => startEdit(e.id, 'commission', e.commission || '', -1)}
                            className={`cursor-pointer px-2 py-1 rounded text-sm ${darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'} transition-colors`}
                          >
                            {e.commission || <span className="text-gray-400 italic">Ajouter</span>}
                          </div>
                        )}
                      </td>
                      {propositionFields.map(f => (
                        <td key={f.key} className="py-2 px-3 min-w-[120px] align-top">
                          {renderMultiCell(e, f.key)}
                        </td>
                      ))}
                      <td className="py-2 px-3 w-[320px] min-w-[320px] align-top">
                        {renderNotesCell(e)}
                      </td>
                      <td className="py-2 px-3 w-[280px] min-w-[280px] align-top">
                        {renderAnnotationsCell(e)}
                      </td>
                      <td className="py-2 px-3">
                        <button
                          onClick={() => openEmployee(e, c)}
                          className={`p-1 rounded-lg ${darkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'} transition-colors`}
                        >
                          <ChevronRight className={`w-4 h-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={15} className={`py-8 text-center text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Aucun candidat trouvé
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail / Edit Modal */}
      {selectedRow && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 z-50 flex items-center justify-center p-4">
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden`}>
            <div className="bg-gradient-anthea p-5 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                <User className="w-6 h-6 text-white" />
                <div>
                  <h2 className="text-lg font-bold text-white">{selectedRow.employee.prenom} {selectedRow.employee.nom}</h2>
                  <p className="text-white/80 text-sm">{selectedRow.convention.nom}</p>
                </div>
              </div>
              <button onClick={() => setSelectedRow(null)} className="text-white/80 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-5 overflow-y-auto flex-1">
              <div>
                <label className={`block text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Statut</label>
                {editingCell?.id === selectedRow.employee.id && editingCell?.field === 'statut' ? (
                  <div className="flex items-center gap-2">
                    <select
                      value={editValue}
                      onChange={(e) => {
                        setEditValue(e.target.value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveEdit();
                        if (e.key === 'Escape') cancelEdit();
                      }}
                      autoFocus
                      className={`flex-1 px-3 py-2 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    >
                      {statuts.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                    <button onClick={saveEdit} className={`p-2 rounded-lg ${darkMode ? 'bg-green-900/40 text-green-300 hover:bg-green-900/60' : 'bg-green-50 text-green-600 hover:bg-green-100'}`}>
                      <Save className="w-4 h-4" />
                    </button>
                    <button onClick={cancelEdit} className={`p-2 rounded-lg ${darkMode ? 'bg-red-900/40 text-red-300 hover:bg-red-900/60' : 'bg-red-50 text-red-600 hover:bg-red-100'}`}>
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <span
                    onClick={() => startEdit(selectedRow.employee.id, 'statut', selectedRow.employee.statut, -1)}
                    className={`inline-block cursor-pointer px-2.5 py-1 rounded-full text-xs font-medium ${darkMode ? statutDarkColors[selectedRow.employee.statut] : statutColors[selectedRow.employee.statut]}`}
                  >
                    {selectedRow.employee.statut}
                  </span>
                )}
              </div>
              <div>
                <label className={`block text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Commission</label>
                {editingCell?.id === selectedRow.employee.id && editingCell?.field === 'commission' ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveEdit();
                        if (e.key === 'Escape') cancelEdit();
                      }}
                      autoFocus
                      placeholder="N° ou mois/année"
                      className={`flex-1 px-3 py-2 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />
                    <button onClick={saveEdit} className={`p-2 rounded-lg ${darkMode ? 'bg-green-900/40 text-green-300 hover:bg-green-900/60' : 'bg-green-50 text-green-600 hover:bg-green-100'}`}>
                      <Save className="w-4 h-4" />
                    </button>
                    <button onClick={cancelEdit} className={`p-2 rounded-lg ${darkMode ? 'bg-red-900/40 text-red-300 hover:bg-red-900/60' : 'bg-red-50 text-red-600 hover:bg-red-100'}`}>
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div
                    onClick={() => startEdit(selectedRow.employee.id, 'commission', selectedRow.employee.commission || '', -1)}
                    className={`cursor-pointer px-3 py-2 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-300 hover:border-blue-500' : 'bg-gray-50 border-gray-200 text-gray-700 hover:border-blue-400'} transition-colors`}
                  >
                    {selectedRow.employee.commission || <span className="text-gray-400 italic">N° ou mois/année</span>}
                  </div>
                )}
              </div>

              <div>
                <label className={`block text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Consultant</label>
                <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{selectedRow.employee.consultant_attribue || '—'}</span>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className={`block text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Âge / Date de naissance</label>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setAgeInputMode('date')}
                      className={`px-2 py-0.5 text-xs rounded-md transition-colors ${ageInputMode === 'date' ? (darkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white') : (darkMode ? 'bg-gray-700 text-gray-400 hover:bg-gray-600' : 'bg-gray-100 text-gray-500 hover:bg-gray-200')}`}
                    >
                      Date de naissance
                    </button>
                    <button
                      onClick={() => setAgeInputMode('age')}
                      className={`px-2 py-0.5 text-xs rounded-md transition-colors ${ageInputMode === 'age' ? (darkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white') : (darkMode ? 'bg-gray-700 text-gray-400 hover:bg-gray-600' : 'bg-gray-100 text-gray-500 hover:bg-gray-200')}`}
                    >
                      Âge
                    </button>
                  </div>
                </div>
                {ageInputMode === 'date' ? (
                  <>
                    {editingCell?.id === selectedRow.employee.id && editingCell?.field === 'date_naissance' ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="date"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveEdit();
                            if (e.key === 'Escape') cancelEdit();
                          }}
                          autoFocus
                          className={`flex-1 px-3 py-2 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        />
                        <button onClick={saveEdit} className={`p-2 rounded-lg ${darkMode ? 'bg-green-900/40 text-green-300 hover:bg-green-900/60' : 'bg-green-50 text-green-600 hover:bg-green-100'}`}>
                          <Save className="w-4 h-4" />
                        </button>
                        <button onClick={cancelEdit} className={`p-2 rounded-lg ${darkMode ? 'bg-red-900/40 text-red-300 hover:bg-red-900/60' : 'bg-red-50 text-red-600 hover:bg-red-100'}`}>
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div
                        onClick={() => startEdit(selectedRow.employee.id, 'date_naissance', selectedRow.employee.date_naissance || '', -1)}
                        className={`cursor-pointer px-3 py-2 rounded-lg border flex items-center gap-2 ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-300 hover:border-blue-500' : 'bg-gray-50 border-gray-200 text-gray-700 hover:border-blue-400'} transition-colors`}
                      >
                        <Calendar className="w-4 h-4 shrink-0" />
                        {selectedRow.employee.date_naissance
                          ? <span>{new Date(selectedRow.employee.date_naissance).toLocaleDateString('fr-FR')} <span className="text-gray-400">({computeAge(selectedRow.employee.date_naissance, null)})</span></span>
                          : <span className="text-gray-400 italic">Cliquer pour ajouter</span>}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {editingCell?.id === selectedRow.employee.id && editingCell?.field === 'age' ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="0"
                          max="120"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveEdit();
                            if (e.key === 'Escape') cancelEdit();
                          }}
                          autoFocus
                          placeholder="Âge en années"
                          className={`flex-1 px-3 py-2 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        />
                        <button onClick={saveEdit} className={`p-2 rounded-lg ${darkMode ? 'bg-green-900/40 text-green-300 hover:bg-green-900/60' : 'bg-green-50 text-green-600 hover:bg-green-100'}`}>
                          <Save className="w-4 h-4" />
                        </button>
                        <button onClick={cancelEdit} className={`p-2 rounded-lg ${darkMode ? 'bg-red-900/40 text-red-300 hover:bg-red-900/60' : 'bg-red-50 text-red-600 hover:bg-red-100'}`}>
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div
                        onClick={() => startEdit(selectedRow.employee.id, 'age', selectedRow.employee.age != null ? String(selectedRow.employee.age) : '', -1)}
                        className={`cursor-pointer px-3 py-2 rounded-lg border flex items-center gap-2 ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-300 hover:border-blue-500' : 'bg-gray-50 border-gray-200 text-gray-700 hover:border-blue-400'} transition-colors`}
                      >
                        <User className="w-4 h-4 shrink-0" />
                        {selectedRow.employee.age != null
                          ? <span>{selectedRow.employee.age} ans</span>
                          : <span className="text-gray-400 italic">Cliquer pour ajouter</span>}
                      </div>
                    )}
                  </>
                )}
              </div>

              <div>
                <label className={`block text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Ville / Dept</label>
                {editingCell?.id === selectedRow.employee.id && editingCell?.field === 'ville_dept' ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveEdit();
                        if (e.key === 'Escape') cancelEdit();
                      }}
                      autoFocus
                      className={`flex-1 px-3 py-2 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />
                    <button onClick={saveEdit} className={`p-2 rounded-lg ${darkMode ? 'bg-green-900/40 text-green-300 hover:bg-green-900/60' : 'bg-green-50 text-green-600 hover:bg-green-100'}`}>
                      <Save className="w-4 h-4" />
                    </button>
                    <button onClick={cancelEdit} className={`p-2 rounded-lg ${darkMode ? 'bg-red-900/40 text-red-300 hover:bg-red-900/60' : 'bg-red-50 text-red-600 hover:bg-red-100'}`}>
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div
                    onClick={() => startEdit(selectedRow.employee.id, 'ville_dept', selectedRow.employee.ville_dept || '', -1)}
                    className={`cursor-pointer px-3 py-2 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-300 hover:border-blue-500' : 'bg-gray-50 border-gray-200 text-gray-700 hover:border-blue-400'} transition-colors`}
                  >
                    {selectedRow.employee.ville_dept ? selectedRow.employee.ville_dept : <span className="text-gray-400 italic">Cliquer pour modifier</span>}
                  </div>
                )}
              </div>

              <div>
                <label className={`block text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Poste actuel</label>
                {editingCell?.id === selectedRow.employee.id && editingCell?.field === 'poste_actuel' ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveEdit();
                        if (e.key === 'Escape') cancelEdit();
                      }}
                      autoFocus
                      className={`flex-1 px-3 py-2 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />
                    <button onClick={saveEdit} className={`p-2 rounded-lg ${darkMode ? 'bg-green-900/40 text-green-300 hover:bg-green-900/60' : 'bg-green-50 text-green-600 hover:bg-green-100'}`}>
                      <Save className="w-4 h-4" />
                    </button>
                    <button onClick={cancelEdit} className={`p-2 rounded-lg ${darkMode ? 'bg-red-900/40 text-red-300 hover:bg-red-900/60' : 'bg-red-50 text-red-600 hover:bg-red-100'}`}>
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div
                    onClick={() => startEdit(selectedRow.employee.id, 'poste_actuel', selectedRow.employee.poste_actuel || '', -1)}
                    className={`cursor-pointer px-3 py-2 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-300 hover:border-blue-500' : 'bg-gray-50 border-gray-200 text-gray-700 hover:border-blue-400'} transition-colors`}
                  >
                    {selectedRow.employee.poste_actuel ? selectedRow.employee.poste_actuel : <span className="text-gray-400 italic">Cliquer pour modifier</span>}
                  </div>
                )}
              </div>

              <RecommendedOffers
                employee={selectedRow.employee}
                propositions={getPropositions(selectedRow.employee)}
                preparedOffers={preparedOffers}
                darkMode={darkMode}
                onAdd={(offer) => addRecommendedOffer(selectedRow.employee, offer)}
              />

              {(() => {
                const props = getPropositions(selectedRow.employee);
                const cleanupOrphanedCandidatures = async () => {
                  setSyncingCleanup(true);
                  try {
                    await removeCandidateFromAllOffers(selectedRow.employee);
                    toast.success('Candidatures orphelines supprimées');
                  } catch {
                    toast.error('Erreur lors du nettoyage');
                  }
                  setSyncingCleanup(false);
                };
                return (
                  <div className="space-y-4">
                    {props.length > 0 && (
                      <div className={`flex items-center gap-2 text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        <Layers className="w-4 h-4" />
                        {props.length} proposition{props.length > 1 ? 's' : ''}
                      </div>
                    )}
                    {props.length === 0 && (
                      <button
                        onClick={cleanupOrphanedCandidatures}
                        disabled={syncingCleanup}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${darkMode ? 'bg-amber-900/30 text-amber-300 hover:bg-amber-900/50 border border-amber-700' : 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200'} ${syncingCleanup ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <Trash2 className="w-4 h-4" />
                        {syncingCleanup ? 'Nettoyage en cours...' : 'Retirer des offres d\'emploi'}
                      </button>
                    )}
                    {props.map((prop, idx) => (
                      <div key={idx} className={`relative rounded-xl border p-4 space-y-3 ${darkMode ? 'border-gray-600 bg-gray-700/50' : 'border-gray-200 bg-gray-50'}`}>
                        <button
                          onClick={() => deleteProposition(selectedRow.employee.id, idx)}
                          className={`absolute top-2 right-2 p-1.5 rounded-lg transition-colors ${darkMode ? 'text-gray-400 hover:text-red-400 hover:bg-gray-600' : 'text-gray-400 hover:text-red-600 hover:bg-red-50'}`}
                          title="Supprimer cette proposition"
                        >
                          <X className="w-4 h-4" />
                        </button>
                        <div className={`text-xs font-semibold pr-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Proposition {props.length > 1 ? `#${idx + 1}` : ''}
                        </div>
                        {propositionFields.map(f => {
                          const isOveField = f.key === 'ove';
                          return (
                          <div key={f.key}>
                            <label className={`block text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>{f.label}</label>
                            {editingCell?.id === selectedRow.employee.id && editingCell?.field === f.key && editingCell?.propIndex === idx ? (
                              <div className="flex items-center gap-2">
                                {isOveField ? (
                                  <select
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') saveEdit();
                                      if (e.key === 'Escape') cancelEdit();
                                    }}
                                    autoFocus
                                    className={`flex-1 px-3 py-2 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                  >
                                    <option value="">—</option>
                                    <option value="OUI">OUI</option>
                                    <option value="NON">NON</option>
                                  </select>
                                ) : (
                                  <input
                                    type="text"
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') saveEdit();
                                      if (e.key === 'Escape') cancelEdit();
                                    }}
                                    autoFocus
                                    className={`flex-1 px-3 py-2 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                  />
                                )}
                                <button onClick={saveEdit} className={`p-2 rounded-lg ${darkMode ? 'bg-green-900/40 text-green-300 hover:bg-green-900/60' : 'bg-green-50 text-green-600 hover:bg-green-100'}`}>
                                  <Save className="w-4 h-4" />
                                </button>
                                <button onClick={cancelEdit} className={`p-2 rounded-lg ${darkMode ? 'bg-red-900/40 text-red-300 hover:bg-red-900/60' : 'bg-red-50 text-red-600 hover:bg-red-100'}`}>
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ) : (
                              <div
                                onClick={() => startEdit(selectedRow.employee.id, f.key, prop[f.key] as string, idx)}
                                className={`cursor-pointer px-3 py-2 rounded-lg border transition-colors ${
                                  isOveField && prop[f.key]
                                    ? prop[f.key] === 'OUI'
                                      ? (darkMode ? 'bg-green-900/30 border-green-700 text-green-300 hover:border-green-500' : 'bg-green-50 border-green-200 text-green-700 hover:border-green-400')
                                      : (darkMode ? 'bg-red-900/30 border-red-700 text-red-300 hover:border-red-500' : 'bg-red-50 border-red-200 text-red-700 hover:border-red-400')
                                    : (darkMode ? 'bg-gray-700 border-gray-600 text-gray-300 hover:border-blue-500' : 'bg-white border-gray-200 text-gray-700 hover:border-blue-400')
                                }`}
                              >
                                {prop[f.key] ? prop[f.key] as string : <span className="text-gray-400 italic">Cliquer pour modifier</span>}
                              </div>
                            )}
                          </div>
                          );
                        })}
                        <div>
                          <label className={`block text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Commentaires Commission</label>
                          {editingCell?.id === selectedRow.employee.id && editingCell?.field === 'notes' && editingCell?.propIndex === idx ? (
                            <div className="flex flex-col gap-2">
                              <textarea
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) saveEdit();
                                  if (e.key === 'Escape') cancelEdit();
                                }}
                                autoFocus
                                rows={5}
                                placeholder="Saisir les commentaires commission ici..."
                                className={`flex-1 min-h-[100px] px-3 py-2 rounded-lg border resize-y break-words ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                              />
                              <div className="flex items-center gap-2">
                                <button onClick={saveEdit} className={`p-2 rounded-lg ${darkMode ? 'bg-green-900/40 text-green-300 hover:bg-green-900/60' : 'bg-green-50 text-green-600 hover:bg-green-100'}`}>
                                  <Save className="w-4 h-4" />
                                </button>
                                <button onClick={cancelEdit} className={`p-2 rounded-lg ${darkMode ? 'bg-red-900/40 text-red-300 hover:bg-red-900/60' : 'bg-red-50 text-red-600 hover:bg-red-100'}`}>
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div
                              onClick={() => startEdit(selectedRow.employee.id, 'notes', prop.notes || '', idx)}
                              className={`cursor-pointer px-3 py-2 rounded-lg border whitespace-pre-wrap min-h-[60px] break-words ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-300 hover:border-blue-500' : 'bg-white border-gray-200 text-gray-700 hover:border-blue-400'} transition-colors`}
                            >
                              {prop.notes ? prop.notes : <span className="text-gray-400 italic">Cliquer pour ajouter des commentaires commission</span>}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Annotations CDP */}
              <div>
                <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>Annotations CDP (non visibles sur l'export)</label>
                {editingCell?.id === selectedRow.employee.id && editingCell?.field === 'annotations_cdp' && editingCell?.propIndex === -1 ? (
                  <div className="flex flex-col gap-2">
                    <textarea
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) saveEdit();
                        if (e.key === 'Escape') cancelEdit();
                      }}
                      autoFocus
                      rows={5}
                      placeholder="Annotations internes du chef de projet..."
                      className={`flex-1 min-h-[100px] px-3 py-2 rounded-lg border resize-y break-words ${darkMode ? 'bg-gray-700 border-amber-700 text-white placeholder-gray-400' : 'bg-amber-50 border-amber-300 text-gray-900 placeholder-gray-400'} focus:outline-none focus:ring-2 focus:ring-amber-500`}
                    />
                    <div className="flex items-center gap-2">
                      <button onClick={saveEdit} className={`p-2 rounded-lg ${darkMode ? 'bg-green-900/40 text-green-300 hover:bg-green-900/60' : 'bg-green-50 text-green-600 hover:bg-green-100'}`}>
                        <Save className="w-4 h-4" />
                      </button>
                      <button onClick={cancelEdit} className={`p-2 rounded-lg ${darkMode ? 'bg-red-900/40 text-red-300 hover:bg-red-900/60' : 'bg-red-50 text-red-600 hover:bg-red-100'}`}>
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    onClick={() => startEdit(selectedRow.employee.id, 'annotations_cdp', selectedRow.employee.annotations_cdp || '', -1)}
                    className={`cursor-pointer px-3 py-2 rounded-lg border whitespace-pre-wrap min-h-[60px] break-words ${darkMode ? 'bg-amber-900/20 border-amber-700 text-amber-200 hover:border-amber-500' : 'bg-amber-50 border-amber-300 text-amber-900 hover:border-amber-400'} transition-colors`}
                  >
                    {selectedRow.employee.annotations_cdp ? selectedRow.employee.annotations_cdp : <span className="text-gray-400 italic">Cliquer pour ajouter des annotations</span>}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => { setShowImportModal(false); setImportPreview([]); setImportConventionId(''); }}>
          <div className={`w-full max-w-4xl max-h-[90vh] flex flex-col rounded-2xl shadow-xl ${darkMode ? 'bg-gray-800' : 'bg-white'}`} onClick={e => e.stopPropagation()}>
            <div className={`flex items-center justify-between p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Importer des salariés</h2>
              <button onClick={() => { setShowImportModal(false); setImportPreview([]); setImportConventionId(''); }} className={`p-1 rounded-lg ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
                <X className={`w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {importPreview.length === 0 ? (
                <>
                  <div>
                    <label className={`block text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Convention *</label>
                    <select
                      value={importConventionId}
                      onChange={e => setImportConventionId(e.target.value)}
                      className={`w-full px-3 py-2 rounded-xl border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-anthea-blue`}
                    >
                      <option value="">— Choisir une convention —</option>
                      {conventions.map(c => (
                        <option key={c.id} value={c.id}>{c.entreprise_nom || c.nom}</option>
                      ))}
                    </select>
                  </div>

                  <div
                    onClick={() => importConventionId && fileInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-xl p-10 text-center transition-colors ${!importConventionId ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${darkMode ? 'border-gray-600 hover:border-gray-500' : 'border-gray-300 hover:border-gray-400'}`}
                  >
                    <Upload className={`w-10 h-10 mx-auto mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                    <p className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                      Glissez un fichier Excel ici ou cliquez pour parcourir
                    </p>
                    <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Formats acceptés: .xlsx, .xls, .csv
                    </p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      className="hidden"
                      onChange={(e) => { if (e.target.files?.[0]) handleImportFileSelected(e.target.files[0]); }}
                    />
                  </div>

                  <div className={`flex items-start gap-2 p-3 rounded-xl ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                    <AlertCircle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                    <div className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      <p className="font-medium mb-1">Colonnes attendues:</p>
                      <p>Nom, Prénom, Tél, Portable, Email, Poste occupé, Âge, Ville / Dept, Commission (N° ou mois/année), Date de début, Date de fin, Consultant attribué, Statut, Commentaires Commission</p>
                    </div>
                  </div>

                  <button
                    onClick={handleDownloadImportTemplate}
                    className={`flex items-center gap-2 text-sm font-medium ${darkMode ? 'text-anthea-lime hover:text-anthea-lime/80' : 'text-anthea-blue hover:text-anthea-blue/80'} transition-colors`}
                  >
                    <FileDown className="w-4 h-4" />
                    Télécharger un template Excel
                  </button>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <p className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                      {importPreview.length} salarié{importPreview.length > 1 ? 's' : ''} à importer — vérifiez et modifiez avant validation
                    </p>
                    <button
                      onClick={() => setImportPreview([])}
                      className={`flex items-center gap-1 text-xs ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'} transition-colors`}
                    >
                      <X className="w-3 h-3" />
                      Annuler l'import
                    </button>
                  </div>

                  <div className="overflow-x-auto rounded-xl border ${darkMode ? 'border-gray-700' : 'border-gray-200'}">
                    <table className="w-full">
                      <thead>
                        <tr className={`text-left text-xs uppercase ${darkMode ? 'text-gray-400 bg-gray-700/50' : 'text-gray-500 bg-gray-50'} border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                          <th className="py-2 px-2 font-semibold w-8">#</th>
                          <th className="py-2 px-2 font-semibold">Prénom</th>
                          <th className="py-2 px-2 font-semibold">Nom</th>
                          <th className="py-2 px-2 font-semibold">Poste actuel</th>
                          <th className="py-2 px-2 font-semibold">Âge</th>
                          <th className="py-2 px-2 font-semibold">Ville / Dept</th>
                          <th className="py-2 px-2 font-semibold">Email</th>
                          <th className="py-2 px-2 font-semibold">Tél</th>
                          <th className="py-2 px-2 font-semibold">Portable</th>
                          <th className="py-2 px-2 font-semibold">Statut</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importPreview.map((row, i) => (
                          <tr key={i} className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                            <td className={`py-1.5 px-2 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{i + 1}</td>
                            <td className={`py-1.5 px-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{row.prenom}</td>
                            <td className={`py-1.5 px-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{row.nom}</td>
                            <td className={`py-1.5 px-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{row.poste_actuel ?? '—'}</td>
                            <td className={`py-1.5 px-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{row.age != null ? `${row.age} ans` : '—'}</td>
                            <td className={`py-1.5 px-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{row.ville_dept || '—'}</td>
                            <td className={`py-1.5 px-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{row.email || '—'}</td>
                            <td className={`py-1.5 px-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{row.telephone || '—'}</td>
                            <td className={`py-1.5 px-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{row.telephone_portable || '—'}</td>
                            <td className={`py-1.5 px-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{row.statut}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className={`flex items-start gap-2 p-3 rounded-xl text-xs ${darkMode ? 'bg-gray-700/50 text-gray-400' : 'bg-gray-50 text-gray-500'}`}>
                    <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0 text-green-500" />
                    <p>Cliquez sur « Importer » pour ajouter ces {importPreview.length} salarié{importPreview.length > 1 ? 's' : ''} à la convention sélectionnée.</p>
                  </div>
                </>
              )}
            </div>

            <div className={`flex justify-between items-center gap-2 p-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex gap-2">
                {importPreview.length > 0 && (
                  <button
                    onClick={handleDownloadImportTemplate}
                    className={`flex items-center gap-2 text-sm font-medium ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'} transition-colors`}
                  >
                    <FileDown className="w-4 h-4" />
                    Template
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <button onClick={() => { setShowImportModal(false); setImportPreview([]); setImportConventionId(''); }} className={`px-4 py-2 rounded-xl text-sm font-medium ${darkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} transition-colors`}>
                  Fermer
                </button>
                {importPreview.length > 0 && (
                  <button
                    onClick={handleConfirmImport}
                    disabled={importing || !importConventionId}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-anthea text-white text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                  >
                    {importing ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    ) : (
                      <Upload className="w-4 h-4" />
                    )}
                    {importing ? 'Import...' : `Importer (${importPreview.length})`}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Reclassement;
